import Autoplay from 'embla-carousel-autoplay';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useGlobal } from '~/hooks/useGlobal';

import { userStore } from '~/stores';

import { AUTOPLAY_DELAY, HERO_BANNER_IMAGE_PARAMS } from './constants';
import styles from './HeroBanner.module.scss';
import type { HeroBannerProps } from './interfaces';

/**
 * Hero banner slider component for the main page.
 * Displays promotional banners with autoplay and pagination.
 */
const HeroBanner = ({ items, className }: HeroBannerProps) => {
  const { isMobile, isTablet } = useGlobal();
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);

  const imageParams = useMemo(() => {
    if (isTablet) {
      return HERO_BANNER_IMAGE_PARAMS.tablet;
    }
    if (isMobile) {
      return HERO_BANNER_IMAGE_PARAMS.mobile;
    }
    return HERO_BANNER_IMAGE_PARAMS.desktop;
  }, [isMobile, isTablet]);

  const plugins = useMemo(
    () => [Autoplay({ delay: AUTOPLAY_DELAY, stopOnInteraction: false })],
    [],
  );

  const onSelect = useCallback(() => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
  }, [api]);

  const resetAnimation = useCallback(() => {
    setAnimationKey((prev) => prev + 1);
  }, []);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  useEffect(() => {
    if (!api) return;

    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.on('pointerDown', resetAnimation);
    api.on('pointerUp', resetAnimation);

    /**
     * Workaround: register a non-passive touchmove on document so that
     * Chrome's compositor marks touchmove events as cancelable.
     * Without this, MCosmetic's stacking context (z-index on .content)
     * causes the compositor to miss Embla's non-passive listener,
     * resulting in cancelable:false which breaks drag.
     */
    const noop = (() => undefined) as EventListener;
    document.addEventListener('touchmove', noop, { passive: false });

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
      api.off('pointerDown', resetAnimation);
      api.off('pointerUp', resetAnimation);
      document.removeEventListener('touchmove', noop);
    };
  }, [api, onSelect, resetAnimation]);

  const showPagination = items && items.length > 1;

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className={classes([styles.container, className])}>
      {/* I DONT KNOW WHY, BUT IT EMPTY DIV FIXED HEADER CORNERS FOR WINDOW CHROME */}
      <div className={styles.mock} />

      <div className={styles.wrapper}>
        <Carousel
          key={userStore.dir}
          opts={{
            loop: items.length > 1,
            direction: userStore.dir,
          }}
          plugins={plugins}
          setApi={setApi}
          className={styles.carouselRoot}
        >
          <CarouselContent className={styles.viewport}>
            {items.map((item) => (
              <CarouselItem key={item.id} className={styles.slide}>
                <Link
                  to={`${item.deepLink}?showProducts=true`}
                  className={styles.link}
                >
                  <CloudflareResponseImage
                    alt={item.name}
                    className={styles.image}
                    loading="eager"
                    src={
                      isMobile || isTablet
                        ? item.previewImageThumb || item.previewImage
                        : item.previewImage
                    }
                    srcParams={imageParams}
                  />
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {showPagination && (
          <div className={styles.pagination}>
            {items.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={classes([
                  styles.dot,
                  index === selectedIndex && styles.dotActive,
                ])}
                onClick={() => scrollTo(index)}
              >
                {index === selectedIndex && (
                  <span
                    key={`${selectedIndex}-${animationKey}`}
                    className={styles.dotProgress}
                    style={
                      {
                        '--autoplay-delay': `${AUTOPLAY_DELAY}ms`,
                      } as React.CSSProperties
                    }
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default observer(HeroBanner);
